import { isProgramOrder } from '../constants'
import { postScreeningAttachOrder } from '@cannect/services/resources/screening/requests'
import { queryClient } from '@cannect/query-client'
import { buildTrailPath, storedScreeningToken, storedTrailSearch } from './paths'

export { isProgramOrder, isProgramProductId } from '../constants'

// O cart_id (uuid do pedido) só serve ao pagamento; depois de pago a trilha segue sem ele.
const withoutCartId = (search: string) => {
  const params = new URLSearchParams(search.startsWith('?') ? search.slice(1) : search)
  params.delete('cart_id')
  const qs = params.toString()
  return qs ? `?${qs}` : ''
}

export const buildProgramSchedulingPath = () =>
  buildTrailPath('agenda', storedScreeningToken() || undefined, withoutCartId(storedTrailSearch()))

// Depois de pago, a trilha mostra a confirmação do pagamento e só então segue para a agenda.
export const buildProgramPaidPath = () =>
  buildTrailPath('pago', storedScreeningToken() || undefined, withoutCartId(storedTrailSearch()))

// Falha mantém o cart_id: "Tentar novamente" volta ao pagamento do mesmo pedido.
export const buildProgramFailedPath = () =>
  buildTrailPath('pagamento-erro', storedScreeningToken() || undefined, storedTrailSearch())

export const paidCheckoutPath = (order: { id?: number; items?: unknown[] } | null | undefined, fallback: string) => {
  if (isProgramOrder(order as { items?: Array<{ product_id?: number; product?: { id?: number } }> })) {
    return buildProgramPaidPath()
  }
  return fallback
}

export type TProgramPaymentSummary = {
  orderId?: number
  method: 'pix' | 'credit'
  amount: number
  installments?: number
}

const PAYMENT_SUMMARY_KEY = '@CANNECT_PROGRAM_PAYMENT_SUMMARY'

// O pedido é limpo do carrinho assim que o pagamento termina; as telas de resultado leem o resumo guardado aqui.
export const saveProgramPaymentSummary = (summary: TProgramPaymentSummary) => {
  try {
    sessionStorage.setItem(PAYMENT_SUMMARY_KEY, JSON.stringify(summary))
  } catch {
    /* sem sessionStorage a tela de resultado só omite o resumo */
  }
}

export const readProgramPaymentSummary = (): TProgramPaymentSummary | null => {
  try {
    const raw = sessionStorage.getItem(PAYMENT_SUMMARY_KEY)
    return raw ? (JSON.parse(raw) as TProgramPaymentSummary) : null
  } catch {
    return null
  }
}

const ATTACH_ATTEMPTS = 8
const ATTACH_DELAY_MS = 1500

const wait = (ms: number) =>
  new Promise<void>((resolve) => {
    setTimeout(resolve, ms)
  })

// A API só vincula o pedido depois de persistir o pagamento (webhook do gateway), que pode chegar alguns
// segundos depois da aprovação que o portal enxerga. Tenta algumas vezes antes de seguir para a agenda.
const attachWithRetry = async (token: string, orderId: number, attemptsLeft: number): Promise<void> => {
  try {
    const next = await postScreeningAttachOrder(token, { order_id: orderId })
    // A trilha decide a tela pelo estado da triagem em cache; sem isto ela ainda enxergaria "aguardando pagamento".
    queryClient.setQueryData(['screening-next', token], next)
  } catch {
    if (attemptsLeft <= 1) return
    await wait(ATTACH_DELAY_MS)
    await attachWithRetry(token, orderId, attemptsLeft - 1)
  }
}

export const attachProgramOrderIfNeeded = async (order?: { id?: number; items?: unknown[] } | null) => {
  if (!isProgramOrder(order as { items?: Array<{ product_id?: number; product?: { id?: number } }> })) return
  const token = storedScreeningToken()
  const orderId = Number(order?.id)
  if (!token || !orderId) return

  await attachWithRetry(token, orderId, ATTACH_ATTEMPTS)
}
